import type { DocumentSidebarSection } from "@/components/document-sidebar";
import type { DsdEditorParsedItem } from "@/utils/dsd-editor/document-items";
import type { DsdEditorCorrectionItem } from "@/utils/dsd-editor/spellcheck";
import { useCallback, useEffect, useState, type RefObject } from "react";

const HORIZONTAL_SCROLL_MARGIN_PX = 12;

const cellIdAttrSelector = (cellId: string) =>
  `[data-cell-id="${CSS.escape(cellId)}"]`;

const getDomDepth = (node: HTMLElement, root: HTMLElement) => {
  let depth = 0;
  let current: HTMLElement | null = node;

  while (current) {
    if (current === root) return depth;
    depth += 1;
    current = current.parentElement;
  }

  return depth;
};

// hidden 도 포함: 표 스크롤 영역(Radix viewport)은 넘침을 감지하기 전까지 overflow-x: hidden 이라
// 감지 직후에 부르면 빠진다. hidden 도 프로그램 스크롤은 되고, 실제로 넘치는 요소만(scrollWidth) 움직인다
const isHorizontallyScrollable = (element: HTMLElement) => {
  const overflowX = getComputedStyle(element).overflowX;
  return (
    (overflowX === "auto" ||
      overflowX === "scroll" ||
      overflowX === "hidden") &&
    element.scrollWidth > element.clientWidth + 1
  );
};

const collectHorizontalScrollers = (
  root: HTMLElement,
  element: HTMLElement,
) => {
  const scrollers: HTMLElement[] = [];
  let parent = element.parentElement;

  while (parent && parent !== root) {
    if (isHorizontallyScrollable(parent)) {
      scrollers.push(parent);
    }
    parent = parent.parentElement;
  }

  return scrollers;
};

const alignHorizontalScrollerToElement = (
  scroller: HTMLElement,
  element: HTMLElement,
  behavior: ScrollBehavior,
) => {
  const scrollerRect = scroller.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  let delta = 0;

  if (elementRect.left < scrollerRect.left + HORIZONTAL_SCROLL_MARGIN_PX) {
    delta = elementRect.left - scrollerRect.left - HORIZONTAL_SCROLL_MARGIN_PX;
  } else if (
    elementRect.right >
    scrollerRect.right - HORIZONTAL_SCROLL_MARGIN_PX
  ) {
    delta =
      elementRect.right - scrollerRect.right + HORIZONTAL_SCROLL_MARGIN_PX;
  }

  if (delta !== 0) {
    scroller.scrollBy({ left: delta, behavior });
  }
};

// 셀이 속한 섹션이 좌측 목차에서 활성화되도록, 섹션 헤더를 상단보다 약간 더 위로 보낼 여유
const SECTION_ACTIVATE_MARGIN_PX = 4;

/** 셀을 더 올려야 할 때도 셀 상단은 스크롤 영역 상단에서 이만큼 아래에 둔다 */
const CELL_TOP_MARGIN_PX = 16;

/**
 * 셀 하단을 스크롤 영역 높이의 `align` 비율 위치에 두는 scrollTop. align 없음 = 셀 중앙 정렬.
 * reserve 가 있으면 셀 하단 아래에 화면(viewportHeight) 안으로 belowPx 가 남도록 그만큼 더 올린다 —
 * 그 아래 근거 창이 열린다. 단 셀 상단이 영역 상단 + 16 위로는 가지 않는다
 */
export const getCellScrollTop = (
  root: { scrollTop: number; clientHeight: number },
  rootTop: number,
  targetRect: { top: number; height: number },
  align?: number,
  reserve?: { belowPx: number; viewportHeight: number },
) => {
  if (align === undefined) {
    return (
      root.scrollTop +
      targetRect.top -
      rootTop -
      root.clientHeight / 2 +
      targetRect.height / 2
    );
  }
  let bottomOffset = root.clientHeight * align;
  if (reserve) {
    const maxBottom = reserve.viewportHeight - rootTop - reserve.belowPx;
    bottomOffset = Math.max(
      targetRect.height + CELL_TOP_MARGIN_PX,
      Math.min(bottomOffset, maxBottom),
    );
  }
  return (
    root.scrollTop + targetRect.top + targetRect.height - rootTop - bottomOffset
  );
};

/** 셀을 감싼 가로 스크롤러(표 스크롤 영역 등)를 안쪽부터 움직여 셀이 보이게 한다 */
export const alignHorizontalScrollersToCell = (
  root: HTMLElement,
  target: HTMLElement,
) => {
  const scrollers = collectHorizontalScrollers(root, target).sort(
    (a, b) => getDomDepth(b, root) - getDomDepth(a, root),
  );
  for (const scroller of scrollers) {
    alignHorizontalScrollerToElement(scroller, target, "auto");
  }
};

/**
 * 문서를 셀로 스크롤한다. align 이 있으면 셀 하단을 그 비율 위치에, 없으면 셀 중앙 정렬.
 * 섹션 헤더 보정(목차 활성화)이 align 보다 우선한다
 */
export const scrollDocumentToCell = (
  root: HTMLElement,
  cellId: string,
  behavior: ScrollBehavior = "smooth",
  align?: number,
  reserveBelowPx?: number,
) => {
  const target = root.querySelector(cellIdAttrSelector(cellId));
  if (!(target instanceof HTMLElement)) return;

  const rootRect = root.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const targetTop = getCellScrollTop(
    root,
    rootRect.top,
    targetRect,
    align,
    reserveBelowPx === undefined
      ? undefined
      : { belowPx: reserveBelowPx, viewportHeight: window.innerHeight },
  );

  // 셀은 보이게 하되, 좌측 목차(activeSection)가 셀의 섹션과 맞도록:
  // 섹션 헤더가 상단 밖으로 나가야 이전 섹션이 밴드에서 빠져 목차가 맞음.
  // 이 보정이 align(중앙·30%)보다 우선한다 — 섹션 상단 근처의 얕은 셀은 헤더-상단 정렬이 되고, 깊은 셀만 align 대로.
  const section = target.closest<HTMLElement>("[data-dsd-section]");
  let top = targetTop;
  if (section) {
    const sectionRect = section.getBoundingClientRect();
    const sectionTop =
      root.scrollTop +
      sectionRect.top -
      rootRect.top +
      SECTION_ACTIVATE_MARGIN_PX;
    top = Math.max(targetTop, sectionTop);
  }

  root.scrollTo({ top, behavior });

  requestAnimationFrame(() => alignHorizontalScrollersToCell(root, target));
};

const scrollElementToTop = (
  container: HTMLElement,
  target: HTMLElement,
  behavior: ScrollBehavior,
  overshootPx = 0,
) => {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  container.scrollTo({
    top: container.scrollTop + targetRect.top - containerRect.top + overshootPx,
    behavior,
  });
};

const scrollElementToCenter = (
  container: HTMLElement,
  target: HTMLElement,
  behavior: ScrollBehavior,
) => {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  container.scrollTo({
    top:
      container.scrollTop +
      targetRect.top -
      containerRect.top -
      container.clientHeight / 2 +
      targetRect.height / 2,
    behavior,
  });
};

export const useDocumentScroll = ({
  items,
  renderedSections,
  scrollContainerRef,
  scrollRequest,
}: {
  items: DsdEditorParsedItem[];
  renderedSections: string[];
  scrollContainerRef: RefObject<HTMLDivElement>;
  scrollRequest?: {
    chunk?: {
      chunkId: string | null;
      requestKey: number;
    };
    cell?: {
      cellId: string | null;
      requestKey: number;
      align?: number;
      reserveBelowPx?: number;
    };
    correction?: {
      item: DsdEditorCorrectionItem | null;
      requestKey: number;
    };
  };
}) => {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    setActiveSection((current) =>
      current && renderedSections.includes(current)
        ? current
        : (renderedSections[0] ?? null),
    );
  }, [renderedSections]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const visibleSet = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id =
            entry.target instanceof HTMLElement
              ? entry.target.dataset.dsdSection
              : undefined;
          if (!id) return;
          if (entry.isIntersecting) visibleSet.add(id);
          else visibleSet.delete(id);
        });

        const topVisible = renderedSections.find((sectionName) =>
          visibleSet.has(sectionName),
        );
        if (topVisible) setActiveSection(topVisible);
      },
      { root: container, rootMargin: "0px 0px -70% 0px", threshold: 0 },
    );

    renderedSections.forEach((sectionName) => {
      const target = container.querySelector(
        `[data-dsd-section="${CSS.escape(sectionName)}"]`,
      );
      if (target) observer.observe(target);
    });

    return () => observer.disconnect();
  }, [renderedSections, scrollContainerRef]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const cellId = scrollRequest?.cell?.cellId;
    if (!container || !cellId) return;

    scrollDocumentToCell(
      container,
      cellId,
      "smooth",
      scrollRequest?.cell?.align,
      scrollRequest?.cell?.reserveBelowPx,
    );
  }, [
    scrollContainerRef,
    scrollRequest?.cell?.cellId,
    scrollRequest?.cell?.requestKey,
    scrollRequest?.cell?.align,
    scrollRequest?.cell?.reserveBelowPx,
  ]);

  const scrollToChunk = useCallback(
    (chunkId: string) => {
      const container = scrollContainerRef.current;
      const target = container?.querySelector(
        `[data-dsd-chunk-id="${CSS.escape(chunkId)}"]`,
      );
      if (!(target instanceof HTMLElement) || !container) return;

      const targetItem = items.find((item) => item.chunkId === chunkId);
      if (targetItem) setActiveSection(targetItem.sectionName);

      scrollElementToTop(container, target, "smooth");
    },
    [items, scrollContainerRef],
  );

  useEffect(() => {
    const chunkId = scrollRequest?.chunk?.chunkId;
    if (!chunkId) return;

    scrollToChunk(chunkId);
  }, [
    scrollRequest?.chunk?.chunkId,
    scrollRequest?.chunk?.requestKey,
    scrollToChunk,
  ]);

  const scrollToCorrection = useCallback(
    (item: DsdEditorCorrectionItem) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      if (item.kind === "cell") {
        scrollDocumentToCell(container, item.id, "smooth");
        return;
      }

      const target = container.querySelector(
        `[data-dsd-correction-id="${CSS.escape(item.id)}"]`,
      );
      if (!(target instanceof HTMLElement)) return;

      scrollElementToCenter(container, target, "smooth");
    },
    [scrollContainerRef],
  );

  useEffect(() => {
    const correctionItem = scrollRequest?.correction?.item;
    if (!correctionItem) return;

    scrollToCorrection(correctionItem);
  }, [
    scrollRequest?.correction?.item,
    scrollRequest?.correction?.requestKey,
    scrollToCorrection,
  ]);

  const scrollToSection = useCallback(
    (section: DocumentSidebarSection) => {
      setActiveSection(section.id);
      const container = scrollContainerRef.current;
      const target = container?.querySelector(
        `[data-dsd-section="${CSS.escape(section.id)}"]`,
      );
      if (!(target instanceof HTMLElement) || !container) return;

      // 섹션 상단을 정확히 맞추면 이전 섹션의 아래 모서리가 관찰 밴드에 닿은 채(isIntersecting) 남아 목차가 한 칸 위를 가리킨다
      scrollElementToTop(
        container,
        target,
        "smooth",
        SECTION_ACTIVATE_MARGIN_PX,
      );
    },
    [scrollContainerRef],
  );

  return {
    activeSection,
    scrollToSection,
  };
};
